import React, { useEffect, useCallback, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { fetchAuditLog } from "../api/client";
import { DateRangePicker } from "../components/DateRangePicker";
import { useAuth } from "../auth/useAuth";
import { AUDIT_FLOW_META, subjectDeepLink } from "../lib/auditFlows";
import type { AuditEntry, AuditFlow, AuditListResponse } from "../api/types";

const PAGE_SIZE = 20;

const ACTION_OPTIONS = [
  { value: "", label: "Tutte" },
  { value: "correction", label: "Correzione" },
  { value: "approve", label: "Approvazione" },
  { value: "reject", label: "Rifiuto/Scarto" },
  { value: "revert", label: "Revert" },
  { value: "discard", label: "Scarto (BSC)" },
  { value: "reprocess", label: "Riprocessa" },
  { value: "reopen", label: "Riapertura" },
  { value: "document_upload", label: "Upload documento" },
  { value: "document_delete", label: "Elimina documento" },
  { value: "conversation_ticket_created", label: "Agente: pratica creata" },
  { value: "conversation_review_requested", label: "Agente: revisione richiesta" },
  { value: "conversation_internal_notification", label: "Agente: notifica interna" },
];

const ACTION_BADGES: Record<string, { label: string; cls: string }> = {
  correction: { label: "Correzione", cls: "badge-info" },
  bsc_correction: { label: "Correzione", cls: "badge-info" },
  approve: { label: "Approvazione", cls: "badge-success" },
  reject: { label: "Rifiuto", cls: "badge-danger" },
  discard: { label: "Scarto", cls: "badge-danger" },
  revert: { label: "Revert", cls: "badge-warning" },
  reprocess: { label: "Riprocessa", cls: "badge-neutral" },
  reopen: { label: "Riapertura", cls: "badge-warning" },
  document_upload: { label: "Upload documento", cls: "badge-info" },
  document_delete: { label: "Elimina documento", cls: "badge-warning" },
  conversation_ticket_created: { label: "Agente: pratica creata", cls: "badge-success" },
  conversation_review_requested: { label: "Agente: revisione richiesta", cls: "badge-warning" },
  conversation_internal_notification: { label: "Agente: notifica interna", cls: "badge-neutral" },
};

/** Contenuto della cella "Oggetto": link profondo costruito da
 *  subject_type/subject_id se presenti; altrimenti fallback ai vecchi
 *  request_id/bsc_practice_id (righe scritte prima dell'estensione
 *  subject_type, o finché il backend non la popola). Una riga senza
 *  soggetto in nessuna delle due forme resta testo semplice, non un link. */
function SubjectCell({ entry }: { entry: AuditEntry }) {
  const deepLink = subjectDeepLink(entry.subject_type, entry.subject_id);
  if (deepLink) {
    const shortId = (entry.subject_id as string).slice(0, 8);
    return (
      <Link to={deepLink} onClick={(e) => e.stopPropagation()}
        className="text-sm" style={{ fontFamily: "var(--font-family-mono)" }}>
        {shortId}…
      </Link>
    );
  }
  if (entry.request_id) {
    return (
      <Link to={`/requests/${entry.request_id}`} onClick={(e) => e.stopPropagation()}
        className="text-sm" style={{ fontFamily: "var(--font-family-mono)" }}>
        {entry.request_id.slice(0, 8)}…
      </Link>
    );
  }
  if (entry.bsc_practice_id) {
    return (
      <Link to={`/bsc/${entry.bsc_practice_id}`} onClick={(e) => e.stopPropagation()}
        className="text-sm" style={{ fontFamily: "var(--font-family-mono)" }}>
        {entry.bsc_practice_id.slice(0, 8)}…
      </Link>
    );
  }
  return <span className="text-muted text-sm">—</span>;
}

export function AuditLog() {
  const { flow: flowParam } = useParams<{ flow?: string }>();
  const flow = flowParam as AuditFlow | undefined;
  const { isPlatformAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parseInt(searchParams.get("page") || "1", 10);
  const dateFrom = searchParams.get("date_from") || "";
  const dateTo = searchParams.get("date_to") || "";
  const action = searchParams.get("action") || "";
  const requestId = searchParams.get("request_id") || "";
  const operatorId = searchParams.get("operator_id") || "";
  // Ambito tenant: solo il platform_admin può sceglierlo ("" = tenant
  // attivo, "*" = tutti). Per chiunque altro non esiste alcun controllo —
  // vede sempre e solo il proprio tenant, come oggi.
  const tenantScopeChoice = isPlatformAdmin ? (searchParams.get("tenant_id") || "") : "";

  const [data, setData] = useState<AuditListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);

  function setFilter(updates: Record<string, string | null>) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v === null || v === "") next.delete(k);
        else next.set(k, v);
      }
      if (!("page" in updates)) next.delete("page");
      return next;
    }, { replace: true });
  }

  // Il flusso è un segmento di rotta (/audit/:flow), non un query param: se
  // l'operatore passa da un flusso all'altro dal menu la pagina resta
  // montata (stessa Route "audit/:flow"), quindi la paginazione di prima non
  // ha più senso per il nuovo elenco — la si azzera.
  const prevFlowRef = useRef(flowParam);
  useEffect(() => {
    if (prevFlowRef.current !== flowParam) {
      prevFlowRef.current = flowParam;
      setFilter({ page: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowParam]);

  const load = useCallback(() => {
    setIsLoading(true);
    fetchAuditLog({
      page,
      size: PAGE_SIZE,
      date_from: dateFrom || undefined,
      date_to: dateTo || undefined,
      action: action || undefined,
      request_id: requestId || undefined,
      operator_id: operatorId || undefined,
      flow,
      tenant_id: tenantScopeChoice || undefined,
    })
      .then((d) => { setData(d); setError(null); })
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [page, dateFrom, dateTo, action, requestId, operatorId, flow, tenantScopeChoice]);

  useEffect(() => { load(); }, [load]);

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0;
  // Mostra la colonna tenant solo se la risposta dice davvero che sta
  // mostrando più tenant — mai dedotto dallo stato locale (tenantScopeChoice
  // potrebbe essere "*" ma la richiesta essere ancora in volo, o il server
  // potrebbe — in teoria — restringere lo scope indipendentemente da cosa è
  // stato chiesto).
  const showTenantColumn = data?.tenant_scope === "*";

  const title = flow ? `Registro attività — ${AUDIT_FLOW_META[flow].label}` : "Registro attività";

  return (
    <div>
      <h1 style={{ fontSize: "var(--font-size-xl)", fontWeight: "var(--font-weight-bold)", marginBottom: "var(--space-4)" }}>
        <span className="material-symbols-outlined" style={{ fontSize: 24, marginRight: 8, verticalAlign: "middle" }}>history</span>
        {title}
      </h1>

      {/* Filters */}
      <div className="card" style={{ marginBottom: "var(--space-3)", padding: "var(--space-3)" }}>
        <div style={{ display: "flex", gap: "var(--space-4)", alignItems: "end", flexWrap: "wrap" }}>
          <div>
            <label className="form-label">Periodo</label>
            <DateRangePicker from={dateFrom} to={dateTo}
              onChange={(f, t) => setFilter({ date_from: f, date_to: t })}
              style={{ height: 36, display: "inline-flex", alignItems: "center" }} />
          </div>
          <div>
            <label className="form-label">Tipo azione</label>
            <select className="form-select" value={action} style={{ height: 36 }}
              onChange={(e) => setFilter({ action: e.target.value || null })}>
              {ACTION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Operatore</label>
            <input className="form-input" type="text" placeholder="ID operatore..." style={{ height: 36 }}
              value={operatorId}
              onChange={(e) => setFilter({ operator_id: e.target.value || null })} />
          </div>
          <div>
            <label className="form-label">Richiesta</label>
            <input className="form-input" type="text" placeholder="ID richiesta..." style={{ height: 36 }}
              value={requestId}
              onChange={(e) => setFilter({ request_id: e.target.value || null })} />
          </div>
          {isPlatformAdmin && (
            <div>
              <label className="form-label">Tenant</label>
              <select className="form-select" value={tenantScopeChoice} style={{ height: 36 }}
                onChange={(e) => setFilter({ tenant_id: e.target.value || null })}>
                <option value="">Tenant attivo</option>
                <option value="*">Tutti i tenant</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Loading */}
      {isLoading && !data && (
        <div className="loading-center"><div className="spinner spinner-lg" /></div>
      )}

      {/* Error */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: "var(--space-3)" }}>
          <span className="material-symbols-outlined" style={{ fontSize: 16, marginRight: 4 }}>error</span>
          {error}
        </div>
      )}

      {/* Table */}
      {data && data.items.length > 0 && (
        <div className="card" style={{ padding: 0, overflow: "hidden", opacity: isLoading ? 0.6 : 1 }}>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Data</th>
                  {showTenantColumn && <th>Tenant</th>}
                  <th>Operatore</th>
                  <th>Azione</th>
                  <th>Oggetto</th>
                  <th>Feedback</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((entry) => {
                  const isExpanded = expandedRow === entry.id;
                  const badge = ACTION_BADGES[entry.action] ?? { label: entry.action, cls: "badge-neutral" };
                  return (
                    <React.Fragment key={entry.id}>
                      <tr onClick={() => setExpandedRow(isExpanded ? null : entry.id)} style={{ cursor: "pointer" }}>
                        <td className="text-sm" style={{ whiteSpace: "nowrap" }}>{formatDate(entry.created_at)}</td>
                        {showTenantColumn && (
                          <td className="text-sm">{entry.tenant_id ?? "—"}</td>
                        )}
                        <td className="text-sm">{entry.operator_name || "—"}</td>
                        <td><span className={`badge ${badge.cls}`}>{badge.label}</span></td>
                        <td>
                          <SubjectCell entry={entry} />
                        </td>
                        <td className="text-sm">
                          {entry.feedback_categories?.length ? entry.feedback_categories.join(", ") : "—"}
                        </td>
                        <td>
                          <div className="truncate text-sm" style={{ maxWidth: 200 }}>
                            {entry.feedback_note || "—"}
                          </div>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr>
                          <td colSpan={showTenantColumn ? 7 : 6} style={{ padding: "var(--space-3)", background: "var(--indaco-gray-50)" }}>
                            <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "var(--space-1) var(--space-3)", fontSize: "var(--font-size-sm)" }}>
                              {entry.feedback_categories?.length && (
                                <>
                                  <span className="text-muted fw-medium">Categorie:</span>
                                  <span>{entry.feedback_categories.join(", ")}</span>
                                </>
                              )}
                              {entry.feedback_note && (
                                <>
                                  <span className="text-muted fw-medium">Note:</span>
                                  <span>{entry.feedback_note}</span>
                                </>
                              )}
                              {entry.lesson_learned && (
                                <>
                                  <span className="text-muted fw-medium">Apprendimento:</span>
                                  <span style={{ color: "var(--indaco-success)" }}>{entry.lesson_learned}</span>
                                </>
                              )}
                              {entry.details && (
                                <>
                                  <span className="text-muted fw-medium">Dettagli:</span>
                                  <pre style={{ fontSize: "var(--font-size-xs)", whiteSpace: "pre-wrap", margin: 0 }}>
                                    {JSON.stringify(entry.details, null, 2)}
                                  </pre>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty */}
      {data && data.items.length === 0 && !isLoading && (
        <div className="card text-center text-muted" style={{ padding: "var(--space-10)" }}>
          Nessuna attività trovata
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button disabled={page <= 1} onClick={() => setFilter({ page: String(page - 1) })}>Prec</button>
          {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
            const p = i + 1;
            return (
              <button key={p} className={page === p ? "active" : ""} onClick={() => setFilter({ page: p === 1 ? null : String(p) })}>
                {p}
              </button>
            );
          })}
          {totalPages > 7 && <span className="text-muted">...</span>}
          <button disabled={page >= totalPages} onClick={() => setFilter({ page: String(page + 1) })}>Succ</button>
        </div>
      )}
    </div>
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}
